import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Play } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { ALERT_CATALOG, type AlertGroupKey, playAlertTone } from "@/lib/alert-types";

export const Route = createFileRoute("/admin/notifications")({
  component: () => (
    <AdminAccessGate action="notificacoes">
      <NotificationsAdminPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Notificações — Administração — Wp Rastreamento" }] }),
});

const GROUP_LABEL: Record<AlertGroupKey, string> = {
  "meus-alertas": "Meus alertas",
  avancados: "Alertas Avançados",
};

function NotificationsAdminPage() {
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  const handlePlay = (key: string, freq: number, beeps: 1 | 2 | 3) => {
    playAlertTone(freq, beeps);
    setPlayingKey(key);
    window.setTimeout(() => setPlayingKey((current) => (current === key ? null : current)), beeps * 300 + 300);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Notificações</h1>
        <p className="text-sm text-muted-foreground">
          Cada tipo de alerta do app dos clientes, com o tom sonoro usado pra identificá-lo.
        </p>
      </div>

      <GpsDriveSessionBanner />

      <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20">
        <CardContent className="pt-6 text-sm text-amber-800 dark:text-amber-300">
          Celulares e navegadores não deixam nenhum app escolher um som diferente por tipo de
          notificação quando o app está fechado — o som do push é sempre o padrão do aparelho, sem
          exceção. Os tons abaixo tocam só aqui, com esta página aberta: servem pra você conhecer o
          "tom" de identificação de cada alerta, não são o som real do celular do cliente.
        </CardContent>
      </Card>

      {(["meus-alertas", "avancados"] as const).map((group) => (
        <Card key={group}>
          <CardHeader>
            <CardTitle className="text-base">{GROUP_LABEL[group]}</CardTitle>
            <CardDescription>
              {group === "meus-alertas"
                ? "Alertas básicos, disponíveis no app de todo cliente."
                : "Alertas de configuração mais avançada."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {ALERT_CATALOG.filter((item) => item.group === group).map((item) => (
              <div
                key={item.key}
                className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-base">
                  {item.emoji}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.label}</span>
                    {!item.traccarTypes && (
                      <Badge variant="secondary" className="text-[10px]">
                        Em breve
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => handlePlay(item.key, item.soundFreq, item.soundBeeps)}
                >
                  <Play className="h-3.5 w-3.5" />
                  {playingKey === item.key ? "Tocando…" : "Tocar"}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
